import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/date';
import { usePendingPromptList } from '@/features/voice-library/api';
import {
  usePilotCliReport,
  useTotalCalls,
  useUserwiseSummary,
} from '../api';

/* ------------------- Userwise summary ------------------- */

export function UserwiseSummaryWidget() {
  const today = useMemo(() => formatDate(new Date()), []);
  const sevenDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatDate(d);
  }, []);
  const [start, setStart] = useState(sevenDaysAgo);
  const [end, setEnd] = useState(today);

  const q = useUserwiseSummary({ startDate: start, endDate: end });
  const rows = q.data ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px]">Start</Label>
          <Input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">End</Label>
          <Input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
        {q.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            No userwise activity in this window.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/60 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left">User</th>
                <th className="px-2 py-1.5 text-right">Campaigns</th>
                <th className="px-2 py-1.5 text-right">Uploaded</th>
                <th className="px-2 py-1.5 text-right">Dialed</th>
                <th className="px-2 py-1.5 text-right">Connected</th>
                <th className="px-2 py-1.5 text-right">DND</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId} className="border-t border-border">
                  <td className="px-2 py-1.5 font-medium">{r.username}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {r.totalCampaigns.toLocaleString()}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {r.numbersUploaded.toLocaleString()}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {r.numbersProcessed.toLocaleString()}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-700">
                    {r.callsConnected.toLocaleString()}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
                    {r.dndCount.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ------------------- Call details (totals) ------------------- */

export function CallDetailsWidget() {
  const today = useMemo(() => formatDate(new Date()), []);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const q = useTotalCalls(start, end);

  const total = q.data?.totalCalls;
  const connected = q.data?.totalConnectedCalls;
  const rate =
    total && total > 0 && typeof connected === 'number'
      ? Math.round((connected / total) * 100)
      : null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px]">Start</Label>
          <Input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">End</Label>
          <Input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-20" />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Tile label="Total calls" value={total ?? 0} tone="neutral" />
          <Tile
            label="Connected"
            value={connected ?? 0}
            tone="positive"
            hint={rate != null ? `${rate}%` : undefined}
          />
        </div>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone: 'neutral' | 'positive';
}) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums',
          tone === 'positive' ? 'text-emerald-700' : 'text-foreground',
        )}
      >
        {value.toLocaleString()}
      </div>
      {hint && (
        <div className="text-[10px] text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}

/* ------------------- Pilot wise CLI ------------------- */

export function PilotCliWidget() {
  const [filter, setFilter] = useState('');
  const q = usePilotCliReport(filter);
  const rows = q.data ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value.replace(/\D/g, '').slice(0, 10))}
          placeholder="Filter by 10-digit CLI"
          className="h-8 pl-7 text-xs"
          inputMode="numeric"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
        {q.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            No CLIs match this filter.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/60 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left">Pilot</th>
                <th className="px-2 py-1.5 text-right">Archived</th>
                <th className="px-2 py-1.5 text-right">Unarchived</th>
                <th className="px-2 py-1.5 text-right">Channels</th>
                <th className="px-2 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const low = (r.unarchived ?? 0) < 1500;
                return (
                  <tr key={r.rowLabels} className="border-t border-border">
                    <td className="px-2 py-1.5 font-mono">{r.rowLabels}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {r.archived.toLocaleString()}
                    </td>
                    <td
                      className={cn(
                        'px-2 py-1.5 text-right font-mono',
                        low && 'font-semibold text-amber-700',
                      )}
                    >
                      <span className="inline-flex items-center justify-end gap-1">
                        {low && <AlertTriangle className="h-3 w-3" />}
                        {r.unarchived.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {r.totalChannels.toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {r.grandTotal.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ------------------- Pending prompts queue ------------------- */

export function PendingPromptsWidget() {
  const q = usePendingPromptList();
  const pending = (q.data ?? []).filter(
    (p) => Number(p.promptStatus) === 0,
  );
  const recent = useMemo(
    () =>
      [...pending]
        .sort((a, b) => (b.promptId ?? 0) - (a.promptId ?? 0))
        .slice(0, 6),
    [pending],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Badge
          variant="outline"
          className={cn(
            'px-1.5 py-0 text-[10px]',
            pending.length > 0
              ? 'bg-amber-100 text-amber-900'
              : 'bg-emerald-100 text-emerald-900',
          )}
        >
          {pending.length} pending
        </Badge>
        <Button asChild size="sm" variant="outline">
          <Link to="/all-voice-files">
            Open queue
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {q.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : recent.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            All clear — nothing waiting for review.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {recent.map((p) => (
              <li
                key={p.promptId}
                className="rounded-md border border-border bg-card px-2 py-1.5"
              >
                <div className="truncate text-xs font-medium text-foreground">
                  {p.fileName ?? p.promptName ?? `#${p.promptId}`}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {p.username ?? `User #${p.userId ?? '—'}`}{' '}
                  · {p.promptCategory}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
